import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { PageMetaProvider } from "@/contexts/PageMetaContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { initTracking } from "@/lib/tracking";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import StickyWhatsApp from "@/components/layout/StickyWhatsApp";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import RoutesList from "@/pages/RoutesList";
import RouteDetail from "@/pages/RouteDetail";
import VehicleDetail from "@/pages/VehicleDetail";
import CarSlugRouter from "@/pages/CarSlugRouter";
import ZoneLanding from "@/pages/ZoneLanding";
import GuidesList from "@/pages/GuidesList";
import GuideDetail from "@/pages/GuideDetail";
import RentalFAQ from "@/pages/RentalFAQ";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";

import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import VehiclesAdmin from "@/pages/admin/VehiclesAdmin";
import RoutesAdmin from "@/pages/admin/RoutesAdmin";
import BookingsAdmin from "@/pages/admin/BookingsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import TrackingAdmin from "@/pages/admin/TrackingAdmin";
import GuidesAdmin from "@/pages/admin/GuidesAdmin";
import CollaboratorsAdmin from "@/pages/admin/CollaboratorsAdmin";
import AiSeoAdmin from "@/pages/admin/AiSeoAdmin";
import LegalPagesAdmin from "@/pages/admin/LegalPagesAdmin";
import ChangePassword from "@/pages/admin/ChangePassword";

import "@/App.css";

function PublicSite() {
  return (
    <>
      <Header />
      <Routes>
        {/* EN */}
        <Route path="/" element={<Home />} />
        <Route path="/luxury-car-rental-costa-blanca" element={<Home />} />
        <Route path="/cars" element={<Home />} />
        <Route path="/cars/:slug" element={<CarSlugRouter />} />
        <Route path="/guides" element={<GuidesList />} />
        <Route path="/guides/:slug" element={<GuideDetail />} />
        <Route path="/rental-faq" element={<RentalFAQ />} />
        <Route path="/routes" element={<RoutesList />} />
        <Route path="/routes/:slug" element={<RouteDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/rental-conditions" element={<LegalPage type="rental_conditions" />} />
        <Route path="/terms-and-conditions" element={<LegalPage type="terms" />} />
        <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
        <Route path="/cookies-policy" element={<LegalPage type="cookies" />} />

        {/* ES */}
        <Route path="/es" element={<Home />} />
        <Route path="/es/alquiler-coches-lujo-costa-blanca" element={<Home />} />
        <Route path="/es/coches" element={<Home />} />
        <Route path="/es/coches/:slug" element={<CarSlugRouter />} />
        <Route path="/es/guias" element={<GuidesList />} />
        <Route path="/es/guias/:slug" element={<GuideDetail />} />
        <Route path="/es/preguntas-frecuentes-alquiler" element={<RentalFAQ />} />
        <Route path="/es/rutas" element={<RoutesList />} />
        <Route path="/es/rutas/:slug" element={<RouteDetail />} />
        <Route path="/es/contacto" element={<Contact />} />
        <Route path="/es/condiciones-alquiler" element={<LegalPage type="rental_conditions" />} />
        <Route path="/es/terminos-condiciones" element={<LegalPage type="terms" />} />
        <Route path="/es/politica-privacidad" element={<LegalPage type="privacy" />} />
        <Route path="/es/politica-cookies" element={<LegalPage type="cookies" />} />

        {/* RU */}
        <Route path="/ru" element={<Home />} />
        <Route path="/ru/arenda-luxury-avto-costa-blanca" element={<Home />} />
        <Route path="/ru/cars" element={<Home />} />
        <Route path="/ru/cars/:slug" element={<CarSlugRouter />} />
        <Route path="/ru/guides" element={<GuidesList />} />
        <Route path="/ru/guides/:slug" element={<GuideDetail />} />
        <Route path="/ru/rental-faq" element={<RentalFAQ />} />
        <Route path="/ru/routes" element={<RoutesList />} />
        <Route path="/ru/routes/:slug" element={<RouteDetail />} />
        <Route path="/ru/contact" element={<Contact />} />
        <Route path="/ru/rental-conditions" element={<LegalPage type="rental_conditions" />} />
        <Route path="/ru/terms-and-conditions" element={<LegalPage type="terms" />} />
        <Route path="/ru/privacy-policy" element={<LegalPage type="privacy" />} />
        <Route path="/ru/cookies-policy" element={<LegalPage type="cookies" />} />

        {/* --- 8-language international routes (Prompt Final Global) ------- */}
        {/* FR / DE / IT / NL / PL mirror EN paths under /<lang>/*. Native
            translations are pulled from AI SEO drafts once published; the
            components already fall back to EN via pick(). */}
        {["fr","de","it","nl","pl"].flatMap((lg) => [
          <Route key={`${lg}-home`} path={`/${lg}`} element={<Home />} />,
          <Route key={`${lg}-cars`} path={`/${lg}/cars`} element={<Home />} />,
          <Route key={`${lg}-car-detail`} path={`/${lg}/cars/:slug`} element={<CarSlugRouter />} />,
          <Route key={`${lg}-guides`} path={`/${lg}/guides`} element={<GuidesList />} />,
          <Route key={`${lg}-guide-detail`} path={`/${lg}/guides/:slug`} element={<GuideDetail />} />,
          <Route key={`${lg}-faq`} path={`/${lg}/rental-faq`} element={<RentalFAQ />} />,
          <Route key={`${lg}-routes`} path={`/${lg}/routes`} element={<RoutesList />} />,
          <Route key={`${lg}-route-detail`} path={`/${lg}/routes/:slug`} element={<RouteDetail />} />,
          <Route key={`${lg}-contact`} path={`/${lg}/contact`} element={<Contact />} />,
          <Route key={`${lg}-legal-rc`} path={`/${lg}/rental-conditions`} element={<LegalPage type="rental_conditions" />} />,
          <Route key={`${lg}-legal-tc`} path={`/${lg}/terms-and-conditions`} element={<LegalPage type="terms" />} />,
          <Route key={`${lg}-legal-pp`} path={`/${lg}/privacy-policy`} element={<LegalPage type="privacy" />} />,
          <Route key={`${lg}-legal-cp`} path={`/${lg}/cookies-policy`} element={<LegalPage type="cookies" />} />,
        ])}

        {/* Fallback */}
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <StickyWhatsApp />
    </>
  );
}

function App() {
  useEffect(() => { initTracking(); }, []);
  // Keep staging/Preview on its own origin. The production canonical is
  // handled by SeoHead instead of a client-side redirect.
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <PageMetaProvider>
            <LanguageProvider>
              <ScrollToTop />
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="vehicles" element={<VehiclesAdmin />} />
                  <Route path="routes" element={<RoutesAdmin />} />
                  <Route path="bookings" element={<BookingsAdmin />} />
                  <Route path="tracking" element={<TrackingAdmin />} />
                  <Route path="collaborators" element={<CollaboratorsAdmin />} />
                  <Route path="guides" element={<GuidesAdmin />} />
                  <Route path="ai-seo" element={<AiSeoAdmin />} />
                  <Route path="settings" element={<SettingsAdmin />} />
                  <Route path="legal" element={<LegalPagesAdmin />} />
                  <Route path="password" element={<ChangePassword />} />
                </Route>
                <Route path="*" element={<PublicSite />} />
              </Routes>
              <Toaster />
            </LanguageProvider>
          </PageMetaProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
